import { useState, useRef, useCallback, useEffect } from "react"
import { MySelector, MyDispatch } from "../../redux/store"
import { useDispatch } from "react-redux"
import { toast } from "sonner"
import { setShowUploadModal } from "../../redux/utilSlice"
import { getAllFiles } from "../../redux/gallerySlice"
import { uploadFiles, resetUploadState } from "../../redux/uploadSlice"

const IMAGE_EXTS = ["jpg", "jpeg", "png", "gif", "webp", "bmp", "heic", "heif"]
const VIDEO_EXTS = ["mp4", "mov", "m4v", "avi", "mkv", "webm", "3gp"]

// Accept anything the browser tags as image/video, plus extension-based
// detection for types the browser leaves untyped (notably HEIC).
const isAcceptedFile = (file: File) => {
  if (file.type.startsWith("image/") || file.type.startsWith("video/")) {
    return true
  }
  const ext = file.name.split(".").pop()?.toLowerCase() || ""
  return IMAGE_EXTS.includes(ext) || VIDEO_EXTS.includes(ext)
}

const UploadModal = () => {
  const dispatch = useDispatch<MyDispatch>()
  const { showUploadModal, currentFolderId } = MySelector((state) => state.util)
  const { eventId, data: eventData } = MySelector((state) => state.event)
  const { eventFolders } = MySelector((state) => state.gallery)
  const {
    uploading,
    uploadProgress,
    files: uploadStatuses,
    failedFileIds,
    skipped,
  } = MySelector((state) => state.upload)

  const [selectedFiles, setSelectedFiles] = useState<File[]>([])
  const [selectedFolder, setSelectedFolder] = useState<string>("")
  const [dragActive, setDragActive] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  // Default the folder dropdown to the folder currently being viewed.
  useEffect(() => {
    if (!showUploadModal) return
    const exists = eventFolders?.some((f: any) => f.id === currentFolderId)
    setSelectedFolder(exists ? currentFolderId : "")
  }, [showUploadModal, currentFolderId, eventFolders])

  // Files are kept as their true originals here; HEIC transcoding and
  // compression now happen inside the upload pipeline (compressed/index copies
  // only). We just filter to supported image/video types on selection.
  const processFiles = useCallback((files: File[]) => {
    const validFiles = files.filter(isAcceptedFile)
    if (validFiles.length !== files.length) {
      toast.error("Only image and video files are allowed")
    }
    setSelectedFiles((prev) => [...prev, ...validFiles])
  }, [])

  const handleDrag = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true)
    } else if (e.type === "dragleave") {
      setDragActive(false)
    }
  }, [])

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setDragActive(false)

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const droppedFiles = Array.from(e.dataTransfer.files)
      processFiles(droppedFiles)
    }
  }, [processFiles])

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const files = Array.from(e.target.files)
      processFiles(files)
    }
  }

  const removeFile = (index: number) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index))
  }

  const handleUpload = async () => {
    if (selectedFiles.length === 0) {
      toast.error("Please select files to upload")
      return
    }
    // Only require folder selection if folders exist
    if (eventFolders && eventFolders.length > 0 && !selectedFolder) {
      toast.error("Please select a folder")
      return
    }
    if (!eventId) {
      toast.error("Event ID not found")
      return
    }
    if (!eventData?.hostId) {
      toast.error("Host ID not found")
      return
    }

    const result = await dispatch(uploadFiles({
      files: selectedFiles,
      eventId,
      folderId: selectedFolder || "", // Use empty string if no folder selected
      hostId: eventData?.hostId
    }))

    if (uploadFiles.fulfilled.match(result)) {
      // Refresh gallery data so newly confirmed files show up.
      dispatch(getAllFiles({ eventId }))

      const hasFailures =
        result.payload.failedFileIds.length > 0 ||
        result.payload.skipped.length > 0

      // Keep the modal open when some files failed/skipped so the user can see
      // what needs a retry; otherwise close and reset.
      if (!hasFailures) {
        setSelectedFiles([])
        setSelectedFolder("")
        dispatch(setShowUploadModal(false))
        dispatch(resetUploadState())
      }
    }
  }

  const closeModal = () => {
    if (!uploading) {
      setSelectedFiles([])
      setSelectedFolder("")
      dispatch(resetUploadState())
      dispatch(setShowUploadModal(false))
    }
  }

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes'
    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
  }

  if (!showUploadModal) return null

  return (
    <div className="fixed inset-0 z-[6000] flex items-center justify-center">
      <div
        className="absolute inset-0 bg-black bg-opacity-75"
        onClick={closeModal}
      />
      <div className="relative bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-md mx-4 max-h-[80vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
            Upload Files
          </h2>
          <button
            onClick={closeModal}
            disabled={uploading}
            className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 disabled:opacity-50"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Folder Selection - Only show if folders exist */}
        {eventFolders && eventFolders.length > 0 && (
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Select Folder
            </label>
            <select
              value={selectedFolder}
              onChange={(e) => setSelectedFolder(e.target.value)}
              className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              disabled={uploading}
            >
              <option value="">Choose a folder...</option>
              {eventFolders.map((folder: any) => (
                <option key={folder.id} value={folder.id}>
                  {folder.name}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* File Drop Zone */}
        <div
          className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
            dragActive
              ? "border-blue-500 bg-blue-50 dark:bg-blue-900/20"
              : "border-gray-300 dark:border-gray-600"
          }`}
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
        >
          <div className="flex flex-col items-center">
            <svg className="w-12 h-12 text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
            </svg>
            <p className="text-gray-600 dark:text-gray-400 mb-2">
              Drag and drop files here, or{" "}
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="text-blue-600 hover:text-blue-500"
                disabled={uploading}
              >
                browse
              </button>
            </p>
            <p className="text-sm text-gray-500 dark:text-gray-500">
              Supports images and videos
            </p>
          </div>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept="image/*,video/*,.heic,.heif"
            onChange={handleFileSelect}
            className="hidden"
            disabled={uploading}
          />
        </div>

        {/* Selected Files */}
        {selectedFiles.length > 0 && (
          <div className="mt-4">
            <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Selected Files ({selectedFiles.length})
            </h3>
            <div className="max-h-32 overflow-y-auto space-y-2">
              {selectedFiles.map((file, index) => (
                <div key={index} className="flex items-center justify-between p-2 bg-gray-50 dark:bg-gray-700 rounded">
                  <div className="flex items-center space-x-3">
                    <div className="flex-shrink-0">
                      {file.type.startsWith('image/') ? (
                        <svg className="w-5 h-5 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                      ) : (
                        <svg className="w-5 h-5 text-purple-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                        </svg>
                      )}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm text-gray-900 dark:text-white truncate">
                        {file.name}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        {formatFileSize(file.size)}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => removeFile(index)}
                    disabled={uploading}
                    className="text-red-500 hover:text-red-700 disabled:opacity-50"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Upload Progress */}
        {uploading && (
          <div className="mt-4">
            <div className="flex justify-between items-center mb-2">
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                Uploading files...
              </span>
              <span className="text-sm text-gray-500 dark:text-gray-400">
                {uploadProgress}%
              </span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2 dark:bg-gray-700">
              <div
                className="bg-blue-600 h-2 rounded-full transition-all duration-300 ease-in-out"
                style={{ width: `${uploadProgress}%` }}
              />
            </div>

            {/* Per-file status */}
            {uploadStatuses.length > 0 && (
              <div className="mt-3 max-h-32 overflow-y-auto space-y-1">
                {uploadStatuses.map((f) => (
                  <div
                    key={f.clientRef}
                    className="flex items-center justify-between text-xs"
                  >
                    <span className="text-gray-700 dark:text-gray-300 truncate mr-2">
                      {f.name}
                    </span>
                    <span
                      className={
                        f.status === "failed" || f.status === "unsupported"
                          ? "text-red-500"
                          : f.status === "skipped"
                          ? "text-yellow-600 dark:text-yellow-500"
                          : f.status === "success"
                          ? "text-green-600 dark:text-green-500"
                          : "text-gray-500 dark:text-gray-400"
                      }
                    >
                      {f.status === "success"
                        ? "Done"
                        : f.status === "failed"
                        ? "Failed"
                        : f.status === "skipped"
                        ? "Skipped"
                        : f.status === "unsupported"
                        ? "Unsupported"
                        : f.status === "compressing"
                        ? "Compressing…"
                        : f.status === "confirming"
                        ? "Finishing…"
                        : `${f.progress}%`}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Failure / skip summary (shown after an upload with problems) */}
        {!uploading && (failedFileIds.length > 0 || skipped.length > 0) && (
          <div className="mt-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md">
            <p className="text-sm font-medium text-red-800 dark:text-red-200 mb-1">
              Some files need attention
            </p>
            {failedFileIds.length > 0 && (
              <p className="text-xs text-red-700 dark:text-red-300">
                {failedFileIds.length} file
                {failedFileIds.length > 1 ? "s" : ""} failed to upload. Please
                try again.
              </p>
            )}
            {skipped.map((s) => (
              <p
                key={s.name}
                className="text-xs text-red-700 dark:text-red-300 truncate"
              >
                {s.name}: {s.reason}
              </p>
            ))}
          </div>
        )}

        {/* Upload Button */}
        <div className="mt-6 flex justify-end space-x-3">
          <button
            onClick={closeModal}
            disabled={uploading}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md disabled:opacity-50 dark:bg-gray-600 dark:text-gray-300 dark:hover:bg-gray-500"
          >
            Cancel
          </button>
          <button
            onClick={handleUpload}
            disabled={
              uploading ||
              selectedFiles.length === 0 ||
              (eventFolders && eventFolders.length > 0 && !selectedFolder)
            }
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md disabled:opacity-50"
          >
            {uploading ? "Uploading..." : "Upload"}
          </button>
        </div>
      </div>
    </div>
  )
}

export default UploadModal
